import { z } from "zod";
import i18n from "i18next";

/**
 * Global Russian error map for Zod v4.
 *
 * Registered once via `z.config({ customError })`, so every `zodResolver`-backed
 * form picks up localized messages without per-schema wiring. The global map has
 * the lowest priority — schema-level messages (`.min(1, "…")`) still win, which is
 * intentional. `customError` runs lazily at parse time, so `t(...)` resolves the
 * active language rather than the one captured at registration.
 */

const t = (key: string, options?: Record<string, unknown>): string =>
  i18n.t(`validation.${key}`, options) as string;

export function registerZodErrorMap(): void {
  z.config({
    customError: (iss) => {
      switch (iss.code) {
        case "invalid_type": {
          if (iss.input === undefined) {
            return t("required");
          }
          return t("invalidType");
        }
        case "too_small": {
          // For strings, a minimum of 1 means "must not be empty".
          if (iss.origin === "string" && Number(iss.minimum) <= 1) {
            return t("required");
          }
          if (iss.origin === "string") {
            return t("tooShort", { min: iss.minimum });
          }
          if (iss.origin === "number") {
            return t("numberTooSmall", { min: iss.minimum });
          }
          return t("tooSmall", { min: iss.minimum });
        }
        case "too_big": {
          if (iss.origin === "string") {
            return t("tooLong", { max: iss.maximum });
          }
          if (iss.origin === "number") {
            return t("numberTooBig", { max: iss.maximum });
          }
          return t("tooBig", { max: iss.maximum });
        }
        case "invalid_value":
          return t("invalidValue");
        case "invalid_format":
          return t("invalidFormat");
        case "not_multiple_of":
          return t("notMultipleOf");
        default:
          // Fall through to the next handler in Zod's precedence chain.
          return undefined;
      }
    },
  });
}
